import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store.js";
import { Layout } from "@/components/Layout.js";
import { LoginPage } from "@/modules/dashboard/LoginPage.js";
import { SsoCallbackPage } from "@/modules/auth/SsoCallbackPage.js";
import { DashboardPage } from "@/modules/dashboard/DashboardPage.js";
import { ProjectListPage } from "@/modules/projects/ProjectListPage.js";
import { ProjectDetailPage } from "@/modules/projects/ProjectDetailPage.js";
import { ProjectWizard } from "@/modules/projects/ProjectWizard.js";
import { AuditPage } from "@/modules/audit/AuditPage.js";
import { CustomersPage } from "@/modules/customers/CustomersPage.js";
import { CustomerDetailPage } from "@/modules/customers/CustomerDetailPage.js";
import { UsersPage } from "@/modules/users/UsersPage.js";
import { KnowledgePage } from "@/modules/knowledge/KnowledgePage.js";
import { ComponentsPage } from "@/modules/components/ComponentsPage.js";
import { FoerderungenPage } from "@/modules/foerderungen/FoerderungenPage.js";
import { AdminPage } from "@/modules/admin/AdminPage.js";
import { RoofDesignerPage } from "@/modules/roofdesigner/RoofDesignerPage.js";
import { KalkulationPage } from "@/modules/costing/KalkulationPage.js";
import type { ReactNode } from "react";

function ProtectedRoute({ children, allowInstaller = false }: { children: ReactNode; allowInstaller?: boolean }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Installateure dürfen nur den Kalkulator sehen — alles andere zurück dorthin.
  if (user?.role === "installer" && !allowInstaller) return <Navigate to="/kalkulation" replace />;
  return <Layout>{children}</Layout>;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso" element={<SsoCallbackPage />} />

        <Route path="/dashboard" element={
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        } />

        <Route path="/projects" element={
          <ProtectedRoute><ProjectListPage /></ProtectedRoute>
        } />

        <Route path="/projects/new" element={
          <ProtectedRoute><ProjectWizard /></ProtectedRoute>
        } />

        <Route path="/projects/:id" element={
          <ProtectedRoute><ProjectDetailPage /></ProtectedRoute>
        } />

        <Route path="/projects/:id/roof-designer" element={
          <ProtectedRoute><RoofDesignerPage /></ProtectedRoute>
        } />

        <Route path="/audit" element={
          <ProtectedRoute><AuditPage /></ProtectedRoute>
        } />

        <Route path="/customers" element={
          <ProtectedRoute><CustomersPage /></ProtectedRoute>
        } />

        <Route path="/customers/:id" element={
          <ProtectedRoute><CustomerDetailPage /></ProtectedRoute>
        } />

        <Route path="/users" element={
          <ProtectedRoute><UsersPage /></ProtectedRoute>
        } />

        <Route path="/knowledge" element={
          <ProtectedRoute><KnowledgePage /></ProtectedRoute>
        } />

        <Route path="/components" element={
          <ProtectedRoute><ComponentsPage /></ProtectedRoute>
        } />

        <Route path="/foerderungen" element={
          <ProtectedRoute><FoerderungenPage /></ProtectedRoute>
        } />

        <Route path="/kalkulation" element={
          <ProtectedRoute allowInstaller><KalkulationPage /></ProtectedRoute>
        } />

        <Route path="/admin" element={
          <ProtectedRoute><AdminPage /></ProtectedRoute>
        } />

        <Route path="/admin/:section" element={
          <ProtectedRoute><AdminPage /></ProtectedRoute>
        } />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
